/**
 * Semantic Typography Components
 *
 * These components abstract away raw Tailwind classes so devs declare
 * INTENT (heading level, text variant) not APPEARANCE (text-xl, text-gray-600).
 *
 * The system handles:
 * - Consistent sizing based on semantic level
 * - Dark mode automatically via CSS tokens
 * - Future: accessibility modes, white labeling
 */

import type { TOC } from '@ember/component/template-only';
import { eq } from '../utilities.ts';

// ============================================================================
// HEADING COMPONENT
// ============================================================================

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

interface HeadingSignature {
  Args: {
    /**
     * Semantic heading level (1-6). Determines both the HTML element
     * and the visual styling. Default: 2
     */
    level?: HeadingLevel;
  };
  Element: HTMLHeadingElement;
  Blocks: {
    default: [];
  };
}

/**
 * Renders a semantic heading with consistent styling.
 *
 * @example Basic usage
 * ```gts
 * <Heading @level={{1}}>Page Title</Heading>
 * <Heading @level={{2}}>Section Title</Heading>
 * <Heading>Default is h2</Heading>
 * ```
 */
const Heading: TOC<HeadingSignature> = <template>
  {{#if (eq @level 1)}}
    <h1 class="text-h1" ...attributes>{{yield}}</h1>
  {{else if (eq @level 2)}}
    <h2 class="text-h2" ...attributes>{{yield}}</h2>
  {{else if (eq @level 3)}}
    <h3 class="text-h3" ...attributes>{{yield}}</h3>
  {{else if (eq @level 4)}}
    <h4 class="text-h4" ...attributes>{{yield}}</h4>
  {{else if (eq @level 5)}}
    <h5 class="text-h4" ...attributes>{{yield}}</h5>
  {{else if (eq @level 6)}}
    <h6 class="text-h4" ...attributes>{{yield}}</h6>
  {{else}}
    {{! Default: unspecified level defaults to h2 }}
    <h2 class="text-h2" ...attributes>{{yield}}</h2>
  {{/if}}
</template>;

// ============================================================================
// TEXT COMPONENT
// ============================================================================

/**
 * Intent determines the semantic meaning/color.
 * Keep this list small - these are the only valid use cases.
 */
type TextIntent = 'default' | 'muted' | 'error' | 'success' | 'warning';

/**
 * Size determines the physical size.
 * Most text should be 'base'. Use sparingly.
 */
type TextSize = 'xs' | 'sm' | 'base';

/**
 * Supported HTML elements for Text component.
 * - span: Inline text (default)
 * - p: Block-level paragraph
 * - label: Form labels
 * - div: Generic block container
 */
type TextTag = 'span' | 'p' | 'label' | 'div';

interface TextSignature {
  Args: {
    /**
     * Semantic meaning of the text. Determines color.
     * - default: Primary content (gray-900/white)
     * - muted: Secondary/helper text (gray-600/gray-400)
     * - error: Error messages (red)
     * - success: Success messages (green)
     * - warning: Warning messages (amber)
     */
    intent?: TextIntent;
    /**
     * Size of the text. Most content should use 'base'.
     * - xs: Fine print, timestamps (12px)
     * - sm: Helper text, captions (14px)
     * - base: Default body text (16px)
     */
    size?: TextSize;
    /**
     * HTML element to render. Defaults to 'span'.
     * - span: Inline text (default)
     * - p: Block-level paragraph
     * - label: Form labels
     * - div: Generic block container
     */
    tag?: TextTag;
  };
  Element: HTMLElement;
  Blocks: {
    default: [];
  };
}

// Intent -> semantic color class (uses our CSS tokens, tio- prefixed to avoid Frontile collision)
const getIntentClass = (intent: TextIntent = 'default'): string => {
  const classes: Record<TextIntent, string> = {
    default: 'tio-text-default',
    muted: 'tio-text-muted',
    error: 'tio-text-error',
    success: 'tio-text-success',
    warning: 'tio-text-warning',
  };
  return classes[intent];
};

// Size -> Tailwind size class
const getSizeClass = (size: TextSize = 'base'): string => {
  const classes: Record<TextSize, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
  };
  return classes[size];
};

/**
 * Renders semantic text with constrained styling options.
 *
 * Devs choose INTENT (what it means) and SIZE (how big).
 * The system handles colors, dark mode, accessibility.
 *
 * @example
 * ```gts
 * <Text>Default body text</Text>
 * <Text @intent="muted">Secondary helper text</Text>
 * <Text @intent="error">Required field</Text>
 * <Text @intent="muted" @size="xs">Timestamp or fine print</Text>
 * <Text @tag="p">Block-level paragraph</Text>
 * <Text @tag="label" @intent="muted" @size="sm">Form label</Text>
 * ```
 */
// TODO: Simplify with native Glimmer `element` helper once TypeScript typing is resolved
const Text: TOC<TextSignature> = <template>
  {{#if (eq @tag "p")}}
    <p class="{{getIntentClass @intent}} {{getSizeClass @size}}" ...attributes>{{yield}}</p>
  {{else if (eq @tag "label")}}
    <label class="{{getIntentClass @intent}} {{getSizeClass @size}}" ...attributes>{{yield}}</label>
  {{else if (eq @tag "div")}}
    <div class="{{getIntentClass @intent}} {{getSizeClass @size}}" ...attributes>{{yield}}</div>
  {{else}}
    <span class="{{getIntentClass @intent}} {{getSizeClass @size}}" ...attributes>{{yield}}</span>
  {{/if}}
</template>;

// ============================================================================
// EXPORTS
// ============================================================================

export { Heading, Text };
